import type { CommonData } from '@/components/blockEditor/blockEditor.types';
import { CURRENT_SCHEMA_VERSION } from '@/components/blockEditor/blockEditor.types';

export function getDefaultEstateTaxesData(): CommonData {
  return {
    version: CURRENT_SCHEMA_VERSION,
    blocks: [
      {
        id: 'estate-taxes-intro',
        type: 'ListItemsSection',
        props: {},
        children: {
          default: [
            {
              id: 'estate-taxes-intro-text',
              type: 'EditableEducationalSlideBody',
              props: {
                text: "Estate taxes are taxes imposed on the transfer of wealth from a deceased person's estate to their heirs or beneficiaries. The tax applies to the total value of the estate, which includes assets like property, cash, investments, and other valuables, before it is distributed. Gift taxes are imposed on the transfer of wealth _during life_.",
              },
            },
          ],
        },
      },
      {
        id: 'estate-taxes-section',
        type: 'ListItemsSection',
        props: {},
        children: {
          default: [
            {
              id: 'estate-taxes-headings',
              type: 'TwoColumnLayout',
              props: {
                leftWidth: '38%',
                rightWidth: '56%',
                columnGap: 4,
              },
              children: {
                leftColumn: [
                  {
                    id: 'estate-taxes-left-heading',
                    type: 'EducationalSlideHeading',
                    props: {
                      title: 'Taxes your estate may be subject to',
                      variant: 'h2',
                    },
                  },
                ],
                rightColumn: [
                  {
                    id: 'estate-taxes-right-heading',
                    type: 'EducationalSlideHeading',
                    props: {
                      title:
                        '...and exemptions that can reduce what is taxable',
                      variant: 'h2',
                    },
                  },
                ],
              },
            },
            {
              id: 'estate-taxes-columns',
              type: 'TwoColumnLayout',
              props: {
                leftWidth: '38%',
                rightWidth: '56%',
                columnGap: 4,
                showDivider: true,
              },
              children: {
                leftColumn: [
                  {
                    id: 'item-federal',
                    type: 'PresentationListItem',
                    props: {
                      title: 'Federal estate tax',
                      description:
                        "The federal estate tax is a tax on the transfer of wealth upon an individual's death. Currently, the federal estate tax has a top rate of 40%. However, this tax applies on the value of your estate above the lifetime exemption threshold.",
                      iconType: 'institution',
                      iconColor: 'NEGATIVE',
                    },
                  },
                  {
                    id: 'item-gst',
                    type: 'PresentationListItem',
                    props: {
                      title: 'Generation-skipping transfer (GST) tax',
                      description:
                        'The generation-skipping transfer (GST) tax is designed to place a limit on the amount that can be passed to persons two or more generations below the donor without incurring taxes. If the donor and recipient are related, the GST tax applies if the recipient is more than one generation below the donor. If they are unrelated, the GST tax applies to any recipients who are at least 37.5 years younger than the donor. GST tax is imposed at a rate of 40% on assets above the exemption.',
                      iconType: 'institution',
                      iconColor: 'NEGATIVE',
                    },
                  },
                  {
                    id: 'item-state',
                    type: 'PresentationListItem',
                    props: {
                      title: 'State gift, estate & inheritance tax',
                      description:
                        'Some states impose their own gift, estate and inheritance tax, of which the rules, exemption amounts and tax rates vary widely from state to state. State gift, estate and inheritance taxes are deductible from federal estate taxes.',
                      iconType: 'institution',
                      iconColor: 'NEGATIVE',
                    },
                  },
                ],
                rightColumn: [
                  {
                    id: 'estate-taxes-exemptions-columns',
                    type: 'TwoColumnLayout',
                    props: {
                      leftWidth: '50%',
                      rightWidth: '50%',
                      columnGap: 4,
                      showDivider: true,
                    },
                    children: {
                      leftColumn: [
                        {
                          id: 'item-lifetime',
                          type: 'PresentationListItem',
                          props: {
                            title: 'Lifetime gift/estate tax exemption',
                            description:
                              'Lifetime estate tax exemption is the amount an individual can transfer during their lifetime and/or upon death without incurring gift or estate tax. As of 2026, this exemption is $15M per individual.',
                            iconType: 'shield',
                            iconColor: 'PRIMARY',
                          },
                        },
                        {
                          id: 'item-annual',
                          type: 'PresentationListItem',
                          props: {
                            title: 'Annual gift exclusion',
                            description:
                              'The annual gift exclusion allows individuals to gift up to a certain amount annually without incurring gift tax. The annual gift exclusion for 2026 is $19,000 per recipient.',
                            iconType: 'shield',
                            iconColor: 'PRIMARY',
                          },
                        },
                        {
                          id: 'item-medical',
                          type: 'PresentationListItem',
                          props: {
                            title: 'Medical and tuition payment exclusion',
                            description:
                              "Medical and tuition exclusions allow individuals to pay unlimited amounts for another's qualified medical expenses or tuition costs without those payments being subject to gift tax, as long as they are made directly to the healthcare provider or educational institution.",
                            iconType: 'shield',
                            iconColor: 'PRIMARY',
                          },
                        },
                      ],
                      rightColumn: [
                        {
                          id: 'item-marital',
                          type: 'PresentationListItem',
                          props: {
                            title: 'Marital deduction and portability',
                            description:
                              "The marital deduction allows an individual to transfer unlimited assets to their spouse during life or after death without incurring gift or estate tax. Portability allows a surviving spouse to use any unused portion of the deceased spouse's estate tax exemption during life and/or at death. Portability does not apply to GST or state estate tax.",
                            iconType: 'shield',
                            iconColor: 'PRIMARY',
                          },
                        },
                        {
                          id: 'item-gst-exemption',
                          type: 'PresentationListItem',
                          props: {
                            title: 'GST exemption',
                            description:
                              'Each individual has a separate lifetime GST exemption, which is $15M per individual as of 2026. This exemption allows individuals to transfer up to the exemption amount in GST-exempt gifts during their lifetime and/or at death without incurring GST tax.',
                            iconType: 'shield',
                            iconColor: 'PRIMARY',
                          },
                        },
                      ],
                    },
                  },
                ],
              },
            },
          ],
        },
      },
    ],
  };
}
